import {inject, Injectable} from '@angular/core';
import {combineLatest, Observable, Subscription} from 'rxjs';
import {distinctUntilChanged, map} from 'rxjs/operators';
import {UploadService} from '../upload/upload.service';
import {DeleteService} from '../delete/delete.service';

/**
 * Zeigt beim Neuladen/Schliessen der Seite eine Browser-Warnung, solange noch Arbeit offen ist, die
 * ein Reload verwirft.
 *
 * <p>Das betrifft Uploads UND Loeschungen, und aus demselben Grund: bei beiden liegt die
 * Warteschlange im Tab. Der Upload kann gar nicht anders (die Bytes stecken im Browser), beim
 * Loeschen laufen die zwei fliegenden Requests serverseitig noch durch, waehrend alles noch
 * Wartende still verloren geht - der Nutzer sieht danach die halbe Auswahl wieder da.</p>
 *
 * <p>Die Warnung ist bewusst kein Ersatz fuer eine serverseitige Warteschlange, sondern nur die
 * ehrliche Zwischenloesung: lieber nachfragen als stillschweigend verwerfen.</p>
 *
 * <p>Registriert wird global in {@code main.ts} nach dem Bootstrap - nicht in einer Komponente,
 * damit der Schutz unabhaengig von der aktuellen Ansicht greift.</p>
 *
 * <p>Hinweis: moderne Browser zeigen einen generischen Text, der vom Browser kontrolliert wird.</p>
 */
@Injectable({providedIn: 'root'})
export class PendingWorkUnloadGuardService {
  private readonly uploadService = inject(UploadService);
  private readonly deleteService = inject(DeleteService);

  private hasPendingWork = false;
  /**
   * Nur die Uploads, getrennt von {@link hasPendingWork}.
   *
   * Die Warnung gilt fuer Uploads UND Loeschungen, die Abbruchmeldung nur fuer Uploads: beim
   * Loeschen laufen die fliegenden Requests serverseitig zu Ende, und das Wartende ist eine Liste
   * im Tab, zu der es serverseitig nichts aufzuraeumen gibt.
   */
  private hasPendingUploads = false;
  private sub?: Subscription;
  private uploadSub?: Subscription;
  private listenerInstalled = false;

  /**
   * Aktiviert den Guard. Ohne Argument werden Uploads und Loeschungen zusammen beobachtet;
   * ein eigenes Observable ist fuer Tests bzw. Sonderfaelle moeglich.
   */
  register(hasPendingWork$: Observable<boolean> = this.defaultPendingWork$()): void {
    if (this.sub) return; // idempotent

    this.uploadSub = this.uploadService.hasActiveUploads$
      .pipe(distinctUntilChanged())
      .subscribe((pending) => {
        this.hasPendingUploads = pending;
      });

    this.sub = hasPendingWork$
      .pipe(distinctUntilChanged())
      .subscribe((pending) => {
        this.hasPendingWork = pending;
        if (pending) {
          this.installListener();
        } else {
          this.uninstallListener();
        }
      });
  }

  unregister(): void {
    this.sub?.unsubscribe();
    this.sub = undefined;
    this.uploadSub?.unsubscribe();
    this.uploadSub = undefined;
    this.hasPendingWork = false;
    this.hasPendingUploads = false;
    this.uninstallListener();
  }

  private defaultPendingWork$(): Observable<boolean> {
    return combineLatest([
      this.uploadService.hasActiveUploads$,
      this.deleteService.hasActiveDeletes$,
    ]).pipe(map(([uploads, deletes]) => uploads || deletes));
  }

  private installListener(): void {
    if (this.listenerInstalled) return;
    window.addEventListener('beforeunload', this.beforeUnloadHandler);
    window.addEventListener('pagehide', this.pageHideHandler);
    this.listenerInstalled = true;
  }

  private uninstallListener(): void {
    if (!this.listenerInstalled) return;
    window.removeEventListener('beforeunload', this.beforeUnloadHandler);
    window.removeEventListener('pagehide', this.pageHideHandler);
    this.listenerInstalled = false;
  }

  private beforeUnloadHandler = (event: BeforeUnloadEvent): string | void => {
    if (!this.hasPendingWork) return;

    // Triggern der nativen Browser-Warnung
    event.preventDefault();
    event.returnValue = '';
    return '';
  };

  /**
   * Die Seite geht jetzt wirklich weg: den abgebrochenen Uploads hinterherraeumen.
   *
   * `pagehide` und nicht `beforeunload`: letzteres feuert schon, wenn der Nutzer das Verlassen
   * ANFRAEGT - er kann im Browser-Dialog immer noch abbrechen, und dann laeuft der Upload weiter.
   * Eine Abbruchmeldung an dieser Stelle wuerde einen laufenden Upload kaputtmelden.
   *
   * `event.persisted` schliesst den bfcache aus: die Seite wird dabei nur eingefroren und kann mit
   * allen laufenden Uploads zurueckkommen. (Ein Dokument mit offenen Verbindungen landet ohnehin
   * kaum im bfcache - aber verlassen wollen wir uns darauf nicht.)
   *
   * Das Loeschen bleibt bewusst aussen vor: dessen fliegende Requests laufen serverseitig zu Ende,
   * und das Wartende ist nur eine Liste im Tab, zu der es serverseitig nichts aufzuraeumen gibt.
   */
  private pageHideHandler = (event: PageTransitionEvent): void => {
    if (!this.hasPendingUploads || event.persisted) return;
    this.uploadService.reportAbortedUploads();
  };
}
